import { useMemo, useState, useEffect } from 'react'
import { Check, ChevronDown, ChevronRight, Search } from 'lucide-react'
import { CefrBadge } from '@/components/CefrBadge'
import { StatusBadge } from '@/components/StatusBadge'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Input } from '@/components/ui/Input'
import { cn } from '@/components/ui/cn'
import { formatMessage } from '@/i18n/format'
import { useMinCopy } from '@/i18n/useUiLocale'
import { groupWordsByDateAdded, groupWordsByLetter } from '@/lib/vocabularyGrouping'
import type { VocabularySortMode } from '@/lib/vocabularySortStorage'
import { VocabularySortToggle } from '@/components/vocabulary/VocabularySortToggle'
import { CollectionFilterSelect } from '@/components/vocabulary/CollectionFilterSelect'
import { highlightMatch } from '@/lib/highlightMatch'
import type { UserWordSummary } from '@/types/user'
import type { CefrLevel } from '@/types/common'

interface WordPickerPanelProps {
  words: UserWordSummary[]
  selectedIds: Set<string>
  onToggle: (id: string) => void
  onSelectMany?: (ids: string[], selected: boolean) => void
  searchQuery: string
  onSearchChange: (query: string) => void
  loading?: boolean
  emptyMessage?: string
  className?: string
  /** Hide MASTERED words from letter groups and show them in a collapsed section at the bottom */
  separateMastered?: boolean
  /** Cap how many words can be selected (e.g. practice generation limit). */
  maxSelection?: number
  /** Label count for “Pick N random”; defaults to maxSelection when omitted. */
  randomPickCount?: number
  /** Replaces selection with a random subset from the pool (used with maxSelection). */
  onPickRandom?: (pool: string[]) => void
  /** Hide the inline “pick random” row under search (e.g. when actions live in a parent footer). */
  hideQuickPick?: boolean
  /** Show A–Z / Recent sort toggle (shares preference with vocabulary list). */
  showSortToggle?: boolean
  sortMode?: VocabularySortMode
  onSortModeChange?: (mode: VocabularySortMode) => void
  /** Show collection filter dropdown (words only — phrases are hidden when a collection is selected). */
  showCollectionFilter?: boolean
  collectionId?: string
  onCollectionChange?: (id: string | undefined) => void
}

function PickerItem({
  word,
  selected,
  onToggle,
  searchQuery,
  dimmed,
  disabled,
}: {
  word: UserWordSummary
  selected: boolean
  onToggle: () => void
  searchQuery: string
  dimmed?: boolean
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onToggle}
      disabled={disabled}
      title={disabled ? 'Selection limit reached' : undefined}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors',
        selected
          ? 'border-primary/60 bg-primary/10'
          : disabled
            ? 'cursor-not-allowed border-transparent opacity-40'
            : 'border-transparent hover:border-border hover:bg-accent/60',
        dimmed && !disabled && 'opacity-80',
      )}
    >
      <div
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
          selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/50',
        )}
      >
        {selected && <Check className="h-3 w-3" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {highlightMatch(word.word, searchQuery)}
        </p>
        {word.translation && (
          <p className="truncate text-xs text-muted-foreground">
            {highlightMatch(word.translation, searchQuery)}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {word.unitKind === 'PHRASE' && (
          <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
            {(word.phraseUnitType ?? 'PHRASE').replaceAll('_', ' ')}
          </span>
        )}
        {word.cefrLevel && (
          <CefrBadge level={word.cefrLevel as CefrLevel} className="hidden text-[10px] sm:inline-flex" />
        )}
        <StatusBadge status={word.status} className="hidden text-[10px] sm:inline-flex" />
      </div>
    </button>
  )
}

function PickerWordGroups({
  groups,
  collapsedGroups,
  onToggleGroup,
  selectedIds,
  onToggle,
  onSelectMany,
  searchQuery,
  maxSelection,
  groupStyle,
}: {
  groups: Map<string, UserWordSummary[]>
  collapsedGroups: Set<string>
  onToggleGroup: (key: string) => void
  selectedIds: Set<string>
  onToggle: (id: string) => void
  onSelectMany?: (ids: string[], selected: boolean) => void
  searchQuery: string
  maxSelection?: number
  groupStyle: 'letter' | 'date'
}) {
  const atLimit = maxSelection != null && selectedIds.size >= maxSelection

  const selectGroup = (groupWords: UserWordSummary[]) => {
    if (!onSelectMany) return
    const ids = groupWords.map((w) => w.id)
    const allSelected = ids.every((id) => selectedIds.has(id))
    if (allSelected) {
      onSelectMany(ids, false)
      return
    }
    const unselected = ids.filter((id) => !selectedIds.has(id))
    onSelectMany(unselected, true)
  }

  return (
    <>
      {Array.from(groups.entries()).map(([groupKey, groupWords]) => {
        const collapsed = collapsedGroups.has(groupKey)
        const selectedInGroup = groupWords.filter((w) => selectedIds.has(w.id)).length

        return (
          <section key={groupKey}>
            <div className="mb-1 flex items-center gap-1">
              <button
                type="button"
                onClick={() => onToggleGroup(groupKey)}
                className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1 py-0.5 text-left hover:bg-accent/50"
              >
                {collapsed ? (
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                )}
                {groupStyle === 'letter' ? (
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/15 text-xs font-bold text-primary">
                    {groupKey}
                  </span>
                ) : (
                  <span className="shrink-0 rounded bg-primary/15 px-1.5 py-0.5 text-xs font-semibold text-primary">
                    {groupKey}
                  </span>
                )}
                <span className="truncate text-xs text-muted-foreground">
                  {groupWords.length}
                  {selectedInGroup > 0 && ` · ${selectedInGroup} selected`}
                </span>
              </button>
              {onSelectMany && (
                <button
                  type="button"
                  onClick={() => selectGroup(groupWords)}
                  disabled={atLimit && selectedInGroup < groupWords.length}
                  className="shrink-0 px-2 py-0.5 text-[10px] text-muted-foreground hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {selectedInGroup === groupWords.length ? 'None' : 'All'}
                </button>
              )}
            </div>
            {!collapsed && (
              <div className="space-y-0.5 pl-1">
                {groupWords.map((word) => (
                  <PickerItem
                    key={word.id}
                    word={word}
                    selected={selectedIds.has(word.id)}
                    onToggle={() => onToggle(word.id)}
                    searchQuery={searchQuery}
                    disabled={atLimit && !selectedIds.has(word.id)}
                  />
                ))}
              </div>
            )}
          </section>
        )
      })}
    </>
  )
}

export function WordPickerPanel({
  words,
  selectedIds,
  onToggle,
  onSelectMany,
  searchQuery,
  onSearchChange,
  loading,
  emptyMessage = 'No words found.',
  className,
  separateMastered = false,
  maxSelection,
  randomPickCount,
  onPickRandom,
  hideQuickPick = false,
  showSortToggle = false,
  sortMode = 'alphabetical',
  onSortModeChange,
  showCollectionFilter = false,
  collectionId,
  onCollectionChange,
}: WordPickerPanelProps) {
  const { practice } = useMinCopy()
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const [masteredOpen, setMasteredOpen] = useState(false)
  const atLimit = maxSelection != null && selectedIds.size >= maxSelection
  const pickLabelCount = randomPickCount ?? maxSelection

  const filteredWords = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return words
    return words.filter(
      (w) =>
        w.word.toLowerCase().includes(q) ||
        w.translation?.toLowerCase().includes(q),
    )
  }, [words, searchQuery])

  const { listWords, masteredWords } = useMemo(() => {
    if (!separateMastered) {
      return { listWords: filteredWords, masteredWords: [] as UserWordSummary[] }
    }
    const active: UserWordSummary[] = []
    const mastered: UserWordSummary[] = []
    for (const w of filteredWords) {
      if (w.status === 'MASTERED') mastered.push(w)
      else active.push(w)
    }
    return { listWords: active, masteredWords: mastered }
  }, [filteredWords, separateMastered])

  const groups = useMemo(() => {
    if (sortMode === 'dateAdded') {
      return groupWordsByDateAdded(listWords)
    }
    return groupWordsByLetter(listWords)
  }, [listWords, sortMode])

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  useEffect(() => {
    setCollapsedGroups(new Set())
  }, [sortMode])

  const masteredSelected = masteredWords.filter((w) => selectedIds.has(w.id)).length

  const selectableWordIds = useMemo(() => {
    const ids = listWords.map((w) => w.id)
    if (separateMastered) {
      ids.push(...masteredWords.map((w) => w.id))
    }
    return ids
  }, [listWords, masteredWords, separateMastered])

  const allVisibleSelected =
    selectableWordIds.length > 0 && selectableWordIds.every((id) => selectedIds.has(id))

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      <div className="shrink-0 space-y-2 px-3 pt-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search vocabulary…"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        {(showCollectionFilter && onCollectionChange) || (showSortToggle && onSortModeChange) ? (
          <div className="flex items-center gap-2">
            {showCollectionFilter && onCollectionChange ? (
              <CollectionFilterSelect
                value={collectionId}
                onChange={onCollectionChange}
                testId="word-picker-collection-filter"
              />
            ) : null}
            {showSortToggle && onSortModeChange ? (
              <VocabularySortToggle
                value={sortMode}
                onChange={onSortModeChange}
                testId="word-picker-sort-toggle"
                className="shrink-0"
              />
            ) : null}
          </div>
        ) : null}
        {onSelectMany && selectableWordIds.length > 0 && !loading && filteredWords.length > 0 && !hideQuickPick && (
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => {
                if (maxSelection != null && onPickRandom) {
                  if (selectedIds.size >= maxSelection) {
                    onSelectMany(selectableWordIds, false)
                  } else {
                    onPickRandom(selectableWordIds)
                  }
                  return
                }
                if (allVisibleSelected) {
                  onSelectMany(selectableWordIds, false)
                  return
                }
                const unselected = selectableWordIds.filter((id) => !selectedIds.has(id))
                onSelectMany(unselected, true)
              }}
              disabled={atLimit && !onPickRandom && !allVisibleSelected}
              className="text-xs font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="word-picker-select-all"
            >
              {maxSelection != null && onPickRandom
                ? selectedIds.size >= maxSelection
                  ? practice.deselectAll
                  : formatMessage(practice.pickRandom, { count: pickLabelCount ?? maxSelection })
                : allVisibleSelected
                  ? practice.deselectAll
                  : maxSelection != null
                    ? `Select up to ${maxSelection}`
                    : 'Select all'}
            </button>
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {loading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : filteredWords.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>
        ) : (
          <div className="space-y-3">
            <PickerWordGroups
              groups={groups}
              collapsedGroups={collapsedGroups}
              onToggleGroup={toggleGroup}
              selectedIds={selectedIds}
              onToggle={onToggle}
              onSelectMany={onSelectMany}
              searchQuery={searchQuery}
              maxSelection={maxSelection}
              groupStyle={sortMode === 'dateAdded' ? 'date' : 'letter'}
            />

            {separateMastered && masteredWords.length > 0 && (
              <section className="mt-2 rounded-lg border border-dashed border-border/80 bg-muted/20">
                <div className="flex items-center gap-1 px-1 py-1">
                  <button
                    type="button"
                    onClick={() => setMasteredOpen((o) => !o)}
                    className="flex flex-1 items-center gap-1.5 rounded-md px-1 py-1 text-left hover:bg-accent/50"
                  >
                    {masteredOpen ? (
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    <span className="text-xs font-medium text-muted-foreground">Mastered</span>
                    <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      {masteredWords.length}
                    </span>
                    {masteredSelected > 0 && (
                      <span className="text-[10px] text-muted-foreground">
                        · {masteredSelected} selected
                      </span>
                    )}
                  </button>
                  {onSelectMany && masteredOpen && (
                    <button
                      type="button"
                      onClick={() => {
                        const ids = masteredWords.map((w) => w.id)
                        const allSelected = ids.every((id) => selectedIds.has(id))
                        if (allSelected) {
                          onSelectMany(ids, false)
                          return
                        }
                        onSelectMany(
                          ids.filter((id) => !selectedIds.has(id)),
                          true
                        )
                      }}
                      disabled={atLimit && masteredSelected < masteredWords.length}
                      className="shrink-0 px-2 py-0.5 text-[10px] text-muted-foreground hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {masteredSelected === masteredWords.length ? 'None' : 'All'}
                    </button>
                  )}
                </div>
                {masteredOpen && (
                  <div className="space-y-0.5 border-t px-2 pb-2 pt-1">
                    {masteredWords
                      .sort((a, b) =>
                        a.word.localeCompare(b.word, undefined, { sensitivity: 'base' }),
                      )
                      .map((word) => (
                        <PickerItem
                          key={word.id}
                          word={word}
                          selected={selectedIds.has(word.id)}
                          onToggle={() => onToggle(word.id)}
                          searchQuery={searchQuery}
                          dimmed
                          disabled={atLimit && !selectedIds.has(word.id)}
                        />
                      ))}
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
